enum BACKEND_NAMES {
    'basic-data-bff' = 'basic-data-bff',
    'bff-service' = 'bff-service',
    'bom-bff' = 'bom-bff',
    'collections-bff' = 'collections-bff',
    'comm-service-bff' = 'comm-service-bff',
    'order-bff' = 'order-bff',
    'report-bff' = 'report-bff',
    'style-bff' = 'style-bff',
    'support-bff' = 'support-bff',
    'user-bff' = 'user-bff',
    'extended-composition-bff' = 'extended-composition-bff',
    'order-core' = 'order-core',
    'colour-core' = 'colour-core',
    'style-core' = 'style-core',
    'basicdata-core' = 'basicdata-core',
}

type BackendBranches = Record<keyof typeof BACKEND_NAMES, string | null>;

/**
 * Valid backend name pattern: lowercase letters, numbers, and hyphens
 * Must contain at least one hyphen (prevents 'constructor', 'prototype', etc.)
 */
const VALID_BACKEND_NAME_REGEX = /^[a-z0-9]+-[a-z0-9-]*$/;

/**
 * Parses backend branch configuration from URL query parameters
 * Expected format: ?branches=bom-bff:BB-9001,style-bff:BB-9002
 *
 * @returns Partial record of backend names to branch names
 */
const parseBranchesFromUrl = (): Record<string, string> => {
    const urlParams = new URLSearchParams(window.location.search);
    const branchesParam = urlParams.get('branches');

    if (!branchesParam) {
        return {};
    }

    const branches: Record<string, string> = {};

    for (const pair of branchesParam.split(',')) {
        const colonIndex = pair.indexOf(':');
        if (colonIndex === -1) continue;

        const backendName = pair.slice(0, colonIndex).trim();
        const branchName = pair.slice(colonIndex + 1).trim();

        if (
            backendName &&
            branchName &&
            VALID_BACKEND_NAME_REGEX.test(backendName)
        ) {
            branches[backendName] = branchName;
        }
    }

    return branches;
};

const getInitialBackendBranches = (): BackendBranches => {
    // Priority order for initial backend branches:
    // 1. URL branches (?branches=...) — highest priority, overrides everything
    // 2. Persisted branches from localStorage — restored from previous session
    // 3. Clean branches (all null) — default fallback
    const cleanBranches = Object.keys(BACKEND_NAMES).reduce(
        (acc, key) => ({ ...acc, [key]: null }),
        {} as BackendBranches
    );

    // URL branches take highest priority — must be resolved before GqlClientProvider
    // creates the Apollo client so that the first query uses the correct branch
    const urlBranches = parseBranchesFromUrl();
    if (Object.keys(urlBranches).length > 0) {
        return { ...cleanBranches, ...urlBranches } as BackendBranches;
    }

    // Fall back to persisted branches from localStorage
    try {
        const stored = localStorage.getItem('backendBranches');
        if (stored) {
            return JSON.parse(stored) as BackendBranches;
        }
    } catch {
        // Fail silently
    }

    return cleanBranches;
};

export { BACKEND_NAMES, getInitialBackendBranches, parseBranchesFromUrl };
export type { BackendBranches };
